import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { getDownloadUrl, fetchImageProxy, getGeneration, toggleFavorite } from '@/api/generation';
import { ApiError } from '@/api/client';
import { APP_NAME } from '@/config/brand';
import { track } from '@/utils/tracker';
import { saveGenerationImageToAlbum } from '@/utils/saveGenerationImage';

interface ResultCardProps {
  imageUrl: string;
  thumbnailUrl?: string;
  styleName: string;
  ratio: string;
  prompt: string;
  onRegenerate: () => void;
  onRegenerateBatch?: () => void;
  onContinueEdit?: () => void;
  onChangeStyle?: () => void;
  onShare?: () => void;
  isVip: boolean;
  genId: string;
  canDownloadHd: boolean;
  favorited?: boolean;
}

export default function ResultCard({
  imageUrl,
  thumbnailUrl,
  styleName,
  ratio,
  prompt,
  onRegenerate,
  onRegenerateBatch,
  onContinueEdit,
  onChangeStyle,
  onShare,
  isVip,
  genId,
  canDownloadHd,
  favorited = false,
}: ResultCardProps) {
  const navigate = useNavigate();
  const [showHdTip, setShowHdTip] = useState(false);
  const { t } = useTranslation();
  const [sdLoading, setSdLoading] = useState(false);
  const [hdLoading, setHdLoading] = useState(false);
  const [isFavorited, setIsFavorited] = useState(favorited);
  const [saveTip, setSaveTip] = useState<'saving' | 'done' | 'failed' | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageSlow, setImageSlow] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageRetryNonce, setImageRetryNonce] = useState(0);
  const [displayImageUrl, setDisplayImageUrl] = useState(thumbnailUrl || imageUrl);
  const sdBlobRef = useRef<Blob | null>(null);

  useEffect(() => {
    setIsFavorited(favorited);
  }, [favorited]);

  useEffect(() => {
    const previewUrl = thumbnailUrl || imageUrl;
    let cancelled = false;

    setDisplayImageUrl(previewUrl);
    setImageLoaded(false);
    setImageSlow(false);
    setImageError(false);

    if (thumbnailUrl && thumbnailUrl !== imageUrl) {
      const img = new Image();
      img.onload = () => {
        if (!cancelled) setDisplayImageUrl(imageUrl);
      };
      img.src = imageUrl;
    }

    return () => { cancelled = true; };
  }, [imageUrl, thumbnailUrl]);

  useEffect(() => {
    if (imageLoaded || imageError) return;
    const timer = window.setTimeout(() => setImageSlow(true), 5000);
    return () => window.clearTimeout(timer);
  }, [displayImageUrl, imageError, imageLoaded, imageRetryNonce]);

  // Pre-fetch SD blob in background so navigator.share() can be called synchronously
  // on iOS Safari (gesture context is lost after any await).
  useEffect(() => {
    let cancelled = false;
    getDownloadUrl(genId, 'sd')
      .then(res => { if (!cancelled) return fetchImageProxy(res.url); })
      .then(blob => { if (!cancelled && blob) sdBlobRef.current = blob; })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [genId]);

  const handleDownload = async (isHd: boolean) => {
    track.resultAction(isHd ? 'save_hd' : 'save_sd', genId);
    if (isHd && !canDownloadHd) { setShowHdTip(true); return; }
    const setLoading = isHd ? setHdLoading : setSdLoading;
    setLoading(true);
    setSaveTip('saving');
    const fileName = `nanoimage2${isHd ? '_hd' : ''}_${Date.now()}.jpg`;

    const doSave = async (blob: Blob) => {
      const file = new File([blob], fileName, { type: blob.type || 'image/jpeg' });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: APP_NAME }).catch(() => {});
      } else {
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      }
    };

    const finish = (status: 'done' | 'failed') => {
      setLoading(false);
      setSaveTip(status);
      setTimeout(() => setSaveTip(null), 2000);
    };

    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
      try {
        const blob = await saveGenerationImageToAlbum({
          genId,
          imageUrl,
          quality: isHd ? 'hd' : 'sd',
        });
        if (!isHd) sdBlobRef.current = blob;
        track.saveSuccess(isHd ? 'hd' : 'sd', genId);
        track.imageSaveSuccess({
          source: 'result_card',
          genId,
          quality: isHd ? 'hd' : 'sd',
          method: blob.saveMethod,
          stage: blob.saveStage,
          durationMs: blob.saveDurationMs,
          bytes: blob.saveBytes,
          httpStatus: blob.saveHttpStatus,
          urlType: blob.saveUrlType,
        });
        finish('done');
      } catch (err) {
        console.error('download failed', err instanceof ApiError ? err.message : err);
        finish('failed');
      }
      return;
    }

    if (!isHd && sdBlobRef.current) {
      try {
        await doSave(sdBlobRef.current);
        track.saveSuccess('sd', genId);
        finish('done');
      } catch (err) {
        console.error('download failed', err instanceof ApiError ? err.message : err);
        finish('failed');
      }
      return;
    }

    try {
      const res = await getDownloadUrl(genId, isHd ? 'hd' : 'sd');
      const blob = await fetchImageProxy(res.url);
      if (!isHd) sdBlobRef.current = blob;
      await doSave(blob);
      track.saveSuccess(isHd ? 'hd' : 'sd', genId);
      finish('done');
    } catch (err) {
      console.error('download failed', err instanceof ApiError ? err.message : err);
      finish('failed');
    }
  };

  const handleFavorite = async () => {
    if (!genId) return;
    const next = !isFavorited;
    setIsFavorited(next);
    track.resultAction(next ? 'favorite' : 'unfavorite', genId);
    track.favoriteToggle(next, genId);
    try {
      const res = await toggleFavorite(genId, next);
      setIsFavorited(res.favorited);
    } catch {
      setIsFavorited(!next);
    }
  };

  const handleShare = async () => {
    track.resultAction('share', genId);
    if (onShare) {
      onShare();
      return;
    }
    if (navigator.share) {
      try {
        await navigator.share({ url: imageUrl, title: APP_NAME, text: prompt });
        track.shareSuccess('result_card_url_share', genId);
      } catch {
        // user cancelled
      }
    }
  };

  const retryImageLoad = async () => {
    setImageLoaded(false);
    setImageSlow(false);
    setImageError(false);
    if (genId) {
      try {
        const refreshed = await getGeneration(genId);
        if (refreshed.image_url) {
          const previewUrl = refreshed.thumbnail_url || refreshed.image_url;
          setDisplayImageUrl(previewUrl);
          if (refreshed.thumbnail_url && refreshed.thumbnail_url !== refreshed.image_url) {
            const img = new Image();
            img.onload = () => setDisplayImageUrl(refreshed.image_url || '');
            img.src = refreshed.image_url;
          }
        }
      } catch {
        // Fall back to retrying the current image URL.
      }
    }
    setImageRetryNonce((value) => value + 1);
  };

  return (
    <div
      data-testid={`generation-result-${genId}`}
      data-generation-id={genId}
      aria-label={`generation-result-${genId}`}
      className="space-y-4"
    >
      {/* 图片展示 */}
      <div className="relative rounded-2xl overflow-hidden bg-gray-50 flex items-center justify-center">
        {!imageLoaded && (
          <div className="min-h-[360px] w-full flex flex-col items-center justify-center px-6 text-center">
            <div className="w-10 h-10 rounded-full flex items-center justify-center mb-3" style={{ background: 'linear-gradient(135deg, #FFE4EF, #F3E8FF)' }}>
              {imageError ? (
                <i className="ri-image-close-line text-pink-500 text-lg" />
              ) : (
                <div className="w-6 h-6 border-2 border-pink-200 border-t-pink-500 rounded-full animate-spin" />
              )}
            </div>
            <p className="text-sm font-semibold text-gray-700">
              {imageError ? '图片加载失败' : imageSlow ? '生成完成，正在加载高清图' : '正在加载图片'}
            </p>
            <p className="text-xs text-gray-400 mt-1 leading-relaxed">
              {imageError ? '网络不稳定时可以重新加载图片。' : '复杂图片体积较大，首次展示可能需要多等一会儿。'}
            </p>
            {(imageSlow || imageError) && (
              <button
                type="button"
                onClick={() => { void retryImageLoad(); }}
                className="mt-3 px-3 py-1.5 rounded-full bg-pink-50 text-pink-500 text-xs font-medium active:bg-pink-100"
              >
                重新加载
              </button>
            )}
          </div>
        )}
        <img
          key={`${displayImageUrl}_${imageRetryNonce}`}
          data-testid="result-image"
          src={displayImageUrl}
          alt="生成结果"
          className="block max-w-full max-h-[70vh] object-contain"
          style={{ display: imageLoaded ? 'block' : 'none' }}
          onLoad={() => {
            setImageLoaded(true);
            setImageSlow(false);
            setImageError(false);
          }}
          onError={() => {
            setImageError(true);
            setImageSlow(false);
          }}
        />
        <div className="absolute top-3 left-3 flex gap-2">
          {styleName.trim() && (
            <span className="px-2 py-1 rounded-full bg-black/50 text-white text-xs">{styleName}</span>
          )}
          <span className="px-2 py-1 rounded-full bg-black/50 text-white text-xs">{ratio}</span>
        </div>
      </div>

      {/* 二次创作闭环 */}
      <div className="rounded-2xl p-3" style={{ background: 'rgba(255,255,255,0.86)', border: '1px solid rgba(255,107,157,0.12)' }}>
        <div className="flex items-center justify-between mb-2.5">
          <div>
            <p className="text-xs font-bold text-gray-800">{t('result_nextTitle')}</p>
            <p className="text-[11px] text-gray-400 mt-0.5">{t('result_nextDesc')}</p>
          </div>
          <div className="px-2 py-1 rounded-full bg-green-50 text-green-500 text-[10px] font-bold flex items-center gap-1">
            <i className="ri-history-line text-xs" />
            <span>{t('result_addedHistory')}</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => { track.resultAction('continue_edit', genId); onContinueEdit?.(); }}
            className="py-3 rounded-xl bg-pink-50 text-pink-500 font-semibold text-xs flex items-center justify-center gap-1.5 cursor-pointer active:scale-95"
          >
            <i className="ri-edit-2-line text-sm" />
            <span>{t('result_continueEdit')}</span>
          </button>
          <button
            onClick={() => { track.resultAction('regenerate_4', genId); onRegenerateBatch ? onRegenerateBatch() : onRegenerate(); }}
            className="py-3 rounded-xl bg-purple-50 text-purple-500 font-semibold text-xs flex items-center justify-center gap-1.5 cursor-pointer active:scale-95"
          >
            <i className="ri-refresh-line text-sm" />
            <span>{t('result_regenerateFour')}</span>
          </button>
          <button
            onClick={() => { track.resultAction('change_style', genId); onChangeStyle?.(); }}
            className="py-3 rounded-xl bg-indigo-50 text-indigo-500 font-semibold text-xs flex items-center justify-center gap-1.5 cursor-pointer active:scale-95"
          >
            <i className="ri-palette-line text-sm" />
            <span>{t('result_changeStyle')}</span>
          </button>
          <button
            onClick={handleFavorite}
            className="py-3 rounded-xl bg-amber-50 text-amber-500 font-semibold text-xs flex items-center justify-center gap-1.5 cursor-pointer active:scale-95"
          >
            <i className={`${isFavorited ? 'ri-star-fill' : 'ri-star-line'} text-sm`} />
            <span>{isFavorited ? t('result_favorited') : t('result_addFavorite')}</span>
          </button>
        </div>
      </div>

      {/* 保存分享按钮 */}
      <div className="grid grid-cols-3 gap-2">
        <button
          data-testid="download-sd-btn"
          onClick={() => handleDownload(false)}
          disabled={sdLoading}
          className="py-3 rounded-xl bg-gray-100 text-gray-700 font-medium text-sm flex items-center justify-center gap-1.5 hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
        >
          {sdLoading ? (
            <i className="ri-loader-4-line animate-spin"></i>
          ) : (
            <i className="ri-download-line"></i>
          )}
          <span>{t('gen_sdDownload')}</span>
        </button>
        <button
          data-testid="download-hd-btn"
          onClick={() => handleDownload(true)}
          disabled={hdLoading}
          className="py-3 rounded-xl font-medium text-sm flex items-center justify-center gap-1.5 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
          style={{ background: 'linear-gradient(135deg, #FF6B9D20, #A855F720)', color: '#A855F7' }}
        >
          {hdLoading ? (
            <i className="ri-loader-4-line animate-spin"></i>
          ) : (
            <i className="ri-vip-crown-line"></i>
          )}
          <span>{t('gen_hdDownload')}</span>
        </button>
        <button
          data-testid="share-btn"
          onClick={handleShare}
          className="py-3 rounded-xl font-medium text-sm flex items-center justify-center gap-1.5 text-white transition-colors cursor-pointer whitespace-nowrap"
          style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}
        >
          <i className="ri-share-line"></i>
          <span>{t('profile_share')}</span>
        </button>
      </div>

      {/* Prompt 展示 */}
      <div className="bg-gray-50 rounded-2xl p-4">
        <p className="text-xs text-gray-400 mb-1">{t('gen_promptLabel')}</p>
        <p className="text-sm text-gray-700 leading-relaxed">{prompt}</p>
      </div>

      {/* 保存成功 toast */}
      {saveTip && (
        <div className="fixed inset-x-0 bottom-20 z-[120] flex justify-center px-5 pointer-events-none">
          <div
            data-testid={`image-save-${saveTip}`}
            aria-label={`image-save-${saveTip}`}
            className="bg-gray-900/90 text-white px-5 py-3 rounded-2xl flex items-center gap-2.5 shadow-lg backdrop-blur-sm"
          >
            {saveTip === 'saving' && (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span className="text-sm font-medium">{t('gen_saving')}</span>
              </>
            )}
            {saveTip === 'done' && (
              <>
                <i className="ri-check-line text-green-400 text-lg" />
                <span className="text-sm font-medium">{t('gen_savedToAlbum')}</span>
              </>
            )}
            {saveTip === 'failed' && (
              <>
                <i className="ri-error-warning-line text-red-300 text-lg" />
                <span className="text-sm font-medium">{t('gen_saveFailed')}</span>
              </>
            )}
          </div>
        </div>
      )}

      {/* HD 下载提示弹层 */}
      {showHdTip && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-5">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowHdTip(false)}></div>
          <div className="relative bg-white rounded-3xl p-6 w-full max-w-sm">
            <div className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #FFE4EF, #F3E8FF)' }}>
              <i className="ri-vip-crown-fill text-pink-500 text-2xl"></i>
            </div>
            <h3 className="text-lg font-bold text-gray-800 text-center mb-2">{t('gen_hdNeedVip')}</h3>
            <p className="text-sm text-gray-500 text-center mb-5">{t('gen_hdNeedVipDesc')}</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowHdTip(false)}
                className="flex-1 py-3 rounded-xl bg-gray-100 text-gray-700 font-medium text-sm cursor-pointer whitespace-nowrap"
              >
                {t('gen_cancel')}
              </button>
              <button
                data-testid="hd-tip-vip-btn"
                onClick={() => {
                  setShowHdTip(false);
                  navigate('/membership');
                }}
                className="flex-1 py-3 rounded-xl text-white font-medium text-sm cursor-pointer whitespace-nowrap"
                style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}
              >
                {t('gen_openMembership')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
